import React from 'react'
import {useTranslation} from 'react-i18next'
import {CategoryPaymentConfig} from '../../models'
import {KTSVG} from 'app/utils/helpers'
import './payment.scss'

interface Props {
  config?: CategoryPaymentConfig | null
  inherited?: boolean
}

const CategoryPaymentSettings: React.FC<Props> = ({config, inherited = false}) => {
  const {t} = useTranslation()
  const label = (key: string) => t(`SERVICE_MANAGEMENT_PAGE.CATEGORY_PAYMENT.${key}`)
  const prePaid = config?.paymentModel === 'PrePaid'
  const showDelay = prePaid && config?.invoiceGeneration === 'Automatic'
  const delay = config?.issueInvoiceAfterServiceEnds
  const invalidDelay =
    showDelay && (delay == null || !Number.isInteger(delay) || delay < 0 || delay > 1440)
  return (
    <section className='category-payment' aria-label={label('TITLE')}>
      {(inherited || !config) && (
        <div className='category-payment__notice'>
          <KTSVG defaultColor path='/assets/images/icons/info-circle-line.svg' />
          <span>{label(inherited ? 'INHERITED' : 'READ_ONLY')}</span>
        </div>
      )}
      {!config ? (
        <p className='category-payment__help' role='status'>
          {label('UNAVAILABLE')}
        </p>
      ) : (
        <>
          <fieldset className='category-payment__group'>
            <legend>
              {label('MODEL')} <span className='category-payment__required'>*</span>
            </legend>
            {(['PostPaid', 'PrePaid'] as const).map((model) => (
              <label className='category-payment__model' key={model}>
                <input type='radio' checked={config.paymentModel === model} disabled />
                <span className='category-payment__radio' aria-hidden='true' />
                <span>{label(model === 'PrePaid' ? 'PRE_PAID' : 'POST_PAID')}</span>
              </label>
            ))}
          </fieldset>
          <fieldset className='category-payment__group'>
            <legend>
              {label('METHOD')} <span className='category-payment__required'>*</span>
            </legend>
            <div className='category-payment__methods'>
              <label className='category-payment__method'>
                <input type='checkbox' checked={prePaid || config.allowOnlinePayment} disabled />
                <span className='category-payment__checkbox' aria-hidden='true'>
                  ✓
                </span>
                <span>{label('ONLINE')}</span>
              </label>
              <label
                className={`category-payment__method${prePaid ? ' is-locked' : ''}`}
                title={prePaid ? label('CASH_TOOLTIP') : undefined}
              >
                <input
                  type='checkbox'
                  aria-label={label('CASH')}
                  checked={!prePaid && config.allowCashPayment}
                  disabled
                />
                <span className='category-payment__checkbox' aria-hidden='true'>
                  ✓
                </span>
                {prePaid && <KTSVG defaultColor path='/assets/images/icons/lock-line.svg' />}
                <span>{label('CASH')}</span>
              </label>
            </div>
          </fieldset>
          <fieldset className='category-payment__group'>
            <legend>
              {label('INVOICE')}
              <span className='category-payment__badge'>{label('MANUAL_DEFAULT')}</span>
            </legend>
            {(['Manual', 'Automatic'] as const).map((mode) => (
              <label className='category-payment__invoice' key={mode}>
                <input type='radio' checked={config.invoiceGeneration === mode} disabled />
                <span className='category-payment__radio' aria-hidden='true' />
                <span>
                  <strong>{label(mode === 'Manual' ? 'MANUAL' : 'AUTOMATIC')}</strong>
                  <small>{label(mode === 'Manual' ? 'MANUAL_HELP' : 'AUTOMATIC_HELP')}</small>
                </span>
              </label>
            ))}
            {showDelay && (
              <label className='category-payment__delay'>
                <span>
                  {label('DELAY')} <span className='category-payment__required'>*</span>
                </span>
                <input
                  type='number'
                  value={delay ?? ''}
                  min={0}
                  max={1440}
                  readOnly
                  aria-invalid={invalidDelay}
                />
                <small>{label('DELAY_HELP')}</small>
                {invalidDelay && (
                  <span className='category-payment__error' role='alert'>
                    {label('INVALID_DELAY')}
                  </span>
                )}
              </label>
            )}
          </fieldset>
        </>
      )}
    </section>
  )
}

export default CategoryPaymentSettings
